import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Truck } from "lucide-react";

import { useGlobalRoutes } from "@/query/hooks/useUnifiedCache";

import {
  DropdownChevron,
  DropdownTrigger,
  SearchableDropdownContent,
} from "./dropdown-primitives";
import { LabeledItem, useLabeledSelect } from "./useLabeledSelect";

type RouteSelectorProps = Readonly<{
  value: string | null;
  onChange: (routeId: string | null) => void;
  /** When false, the global routes fetch is skipped. Defaults to true. */
  enabled?: boolean;
}>;

/**
 * Selector for delivery routes (binds pre_order.route_id). Pulls from the
 * global routes cache so all instances share a single query. Distinct from
 * shipping-address editing, which lives in ShippingAddressSelectorCell (also
 * rendered by PreOrderControls) and binds pre_order.shipping_address_id.
 */
export function RouteSelector({
  value,
  onChange,
  enabled = true,
}: RouteSelectorProps) {
  const { t } = useTranslation();

  const { data: routes = [] } = useGlobalRoutes({ enabled });

  const labeledRoutes = useMemo<LabeledItem<string>[]>(
    () => routes.map((r) => ({ id: r.id, name: r.name, code: r.code })),
    [routes],
  );

  const { search, setSearch, displayLabel, items, dd } = useLabeledSelect({
    items: labeledRoutes,
    value,
    onChange,
    noneLabel: t("ordersRecap.noRoute"),
  });

  return (
    <>
      <DropdownTrigger
        ref={dd.triggerRef}
        open={dd.open}
        onClick={(e) => {
          e.stopPropagation();
          dd.setOpen(!dd.open);
        }}
      >
        <Truck className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" />
        <span className="truncate">{displayLabel}</span>
        <DropdownChevron open={dd.open} className="ml-auto" />
      </DropdownTrigger>

      <SearchableDropdownContent
        dd={dd}
        searchQuery={search}
        onSearchChange={setSearch}
        items={items}
        value={value}
        onChange={onChange}
        emptyMessage={t("ordersRecap.noRouteFound", "No route found.")}
        searchPlaceholder={t("ordersRecap.searchRoute", "Search route...")}
        hasSearchIcon={false}
        maxHeight="200px"
      />
    </>
  );
}
